// ================================================================
// 👥 CLIENTS REPORT - Relatório de Clientes
// ================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Alert, Button } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import { Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import StatCard from '../../components/common/StatCard';
import FilterBar from '../../components/reports/FilterBar';
import FilterDrawer from '../../components/reports/FilterDrawer';
import SmartTable from '../../components/reports/SmartTable';
import ReportsService from '../../services/api/reports';
import MasterLayout from '../../masterLayout/MasterLayout';
import '../../components/reports/Reports.css';
import { useChartsColors } from '../../hooks/useChartsColors';
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, ArcElement, Title, Tooltip, Legend);

const ClientsReport = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [clientsData, setClientsData] = useState(null);
  const [filters, setFilters] = useState({
    period: '30d',
    advancedFilters: {}
  });
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const chartsColors = useChartsColors();

  const loadClientsReportWithFilters = useCallback(async (filtersToUse) => {
    try {
      setLoading(true);
      setError(null);

      console.log('👥 Carregando Relatório de Clientes com filtros...', filtersToUse);

      const result = await ReportsService.getClientsReport({
        period: filtersToUse.period,
        user_id: filtersToUse.advancedFilters?.user_id
      });

      if (result.success) {
        setClientsData(result.data);
        console.log('✅ Relatório de Clientes carregado com filtros:', result.data);
      } else {
        throw new Error(result.message || 'Erro ao carregar relatório');
      }
    } catch (err) {
      console.error('❌ Erro ao carregar relatório:', err);
      setError(err.response?.data?.message || err.message || 'Erro ao carregar relatório');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadClientsReport = useCallback(async () => {
    loadClientsReportWithFilters(filters);
  }, [filters, loadClientsReportWithFilters]);

  useEffect(() => {
    loadClientsReport();
  }, [loadClientsReport]);

  const handlePeriodChange = (newPeriod) => {
    setFilters(prev => {
      const newFilters = { ...prev, period: newPeriod };
      setTimeout(() => loadClientsReportWithFilters(newFilters), 50);
      return newFilters;
    });
  };

  const handleAdvancedFilters = (advancedFilters) => {
    setFilters(prev => {
      const newFilters = { ...prev, advancedFilters };
      setTimeout(() => loadClientsReportWithFilters(newFilters), 50);
      return newFilters;
    });
  };

  const clientsTrendData = clientsData?.charts?.clientsTrend ? {
    labels: clientsData.charts.clientsTrend.map(item => new Date(item.date).toLocaleDateString('pt-BR')),
    datasets: [{
      label: 'Novos Clientes',
      data: clientsData.charts.clientsTrend.map(item => item.new_clients),
      borderColor: '#007bff',
      backgroundColor: 'rgba(0, 123, 255, 0.1)',
      tension: 0.4
    }, {
      label: 'Pedidos Totais',
      data: clientsData.charts.clientsTrend.map(item => item.total_orders),
      borderColor: '#28a745',
      backgroundColor: 'rgba(40, 167, 69, 0.1)',
      tension: 0.4
    }]
  } : { labels: [], datasets: [] };

  const clientsByStatusData = clientsData?.charts?.clientsByStatus ? {
    labels: clientsData.charts.clientsByStatus.map(item => item.status),
    datasets: [{
      label: 'Clientes',
      data: clientsData.charts.clientsByStatus.map(item => item.count),
      backgroundColor: ['#007bff', '#28a745', '#ffc107'],
      borderWidth: 2,
      borderColor: '#fff'
    }]
  } : { labels: [], datasets: [] };

  const clientsColumns = [
    {
      key: 'customer_name',
      label: 'Cliente',
      sortable: true,
      render: (value, row) => (
        <div>
          <strong>{value}</strong>
          <br />
          <small style={{ color: '#6c757d' }}>{row.customer_email}</small>
        </div>
      )
    },
    {
      key: 'client_type',
      label: 'Tipo',
      sortable: true,
      render: (value) => (
        <span style={{
          padding: '4px 12px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '600',
          backgroundColor: value === 'Recorrente' ? '#d4edda' : '#fff3cd',
          color: value === 'Recorrente' ? '#155724' : '#856404'
        }}>
          {value}
        </span>
      )
    },
    {
      key: 'total_orders',
      label: 'Pedidos',
      sortable: true
    },
    {
      key: 'total_spent_formatted',
      label: 'Valor Total',
      sortable: true
    },
    {
      key: 'first_purchase',
      label: 'Primeira Compra',
      sortable: true,
      render: (value) => value ? new Date(value).toLocaleDateString('pt-BR') : 'N/A'
    },
    {
      key: 'last_purchase',
      label: 'Última Compra',
      sortable: true,
      render: (value) => value ? new Date(value).toLocaleDateString('pt-BR') : 'N/A'
    }
  ];

  return (
    <MasterLayout>
      <Container fluid className="reports-container" style={{ padding: '24px', maxWidth: '100%' }}>
        {/* Header */}
        <Row className="mb-4">
          <Col>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Icon icon="mdi:account-group" style={{ fontSize: '22px', color: '#007bff' }} />
                <span className='text-contrast' style={{ fontSize: '22px', fontWeight: '700', display: 'block' }}>
                  Relatório de Clientes
                </span>
              </div>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <Button
                  variant="outline-primary"
                  onClick={() => setShowFilterDrawer(true)}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', borderRadius: '8px', padding: '10px 20px' }}
                  disabled={loading}
                >
                  <Icon icon="mdi:filter-variant" style={{ fontSize: '20px' }} />
                  Filtros Avançados
                  {Object.keys(filters.advancedFilters).length > 0 && (
                    <span style={{
                      backgroundColor: '#007bff',
                      color: '#fff',
                      borderRadius: '50%',
                      width: '20px',
                      height: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}>
                      {Object.keys(filters.advancedFilters).length}
                    </span>
                  )}
                </Button>
                <Button
                  variant="outline-success"
                  onClick={() => console.log('Exportar CSV')}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', borderRadius: '8px', padding: '10px 20px' }}
                  disabled={loading}
                >
                  <Icon icon="mdi:file-excel" style={{ fontSize: '20px' }} />
                  Exportar CSV
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={loadClientsReport}
                  disabled={loading}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', borderRadius: '8px', padding: '10px 20px' }}
                >
                  <Icon icon="mdi:refresh" style={{ fontSize: '20px' }} />
                  Atualizar
                </Button>
              </div>
            </div>
          </Col>
        </Row>

        {/* Filter Bar */}
        <FilterBar
          period={filters.period}
          onPeriodChange={handlePeriodChange}
          onApplyAdvancedFilters={handleAdvancedFilters}
          onRefresh={loadClientsReport}
          loading={loading}
          advancedFilters={filters.advancedFilters}
          onUserFilterChange={() => {}}
        />

        <hr style={{ margin: '24px 0', borderTop: '2px solid #dee2e6' }} />

        {/* Error State */}
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            <Icon icon="mdi:alert-circle" style={{ marginRight: '8px' }} />
            {error}
          </Alert>
        )}

        {/* Loading State */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
              <span className="visually-hidden">Carregando...</span>
            </div>
            <p style={{ marginTop: '16px', color: '#6c757d' }}>Carregando dados dos clientes...</p>
          </div>
        )}

        {/* Main Content */}
        {!loading && clientsData && (
          <>
            {/* KPIs */}
            <Row className="mb-4">
              <Col xs={12} sm={6} lg={3} className="mb-3">
                <StatCard
                  title={clientsData.kpis.total_clients.label}
                  value={clientsData.kpis.total_clients.value}
                  icon="mdi:account-group"
                  color="#007bff"
                />
              </Col>
              <Col xs={12} sm={6} lg={3} className="mb-3">
                <StatCard
                  title={clientsData.kpis.new_clients.label}
                  value={clientsData.kpis.new_clients.value}
                  icon="mdi:account-plus"
                  color="#28a745"
                />
              </Col>
              <Col xs={12} sm={6} lg={3} className="mb-3">
                <StatCard
                  title={clientsData.kpis.avg_ticket.label}
                  value={clientsData.kpis.avg_ticket.formatted}
                  icon="mdi:currency-usd"
                  color="#17a2b8"
                />
              </Col>
              <Col xs={12} sm={6} lg={3} className="mb-3">
                <StatCard
                  title={clientsData.kpis.recurring_clients.label}
                  value={clientsData.kpis.recurring_clients.value}
                  icon="mdi:repeat"
                  color="#ffc107"
                />
              </Col>
            </Row>

            {/* Charts */}
            <Row className="mb-4">
              <Col md={8} className="mb-4">
                <Card style={{ borderRadius: '12px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                  <Card.Body>
                    <span className='text-contrast' style={{ fontSize: '18px', fontWeight: '600', display: 'block', marginBottom: '16px' }}>
                      Tendência de Clientes
                    </span>
                    <Line data={clientsTrendData} options={{ responsive: true, maintainAspectRatio: true, scales: { x: { grid: { color: chartsColors } }, y: { grid: { color: chartsColors }}} }} />
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4} className="mb-4">
                <Card style={{ borderRadius: '12px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                  <Card.Body>
                    <span className='text-contrast' style={{ fontSize: '18px', fontWeight: '600', display: 'block', marginBottom: '16px' }}>
                      Clientes por Status
                    </span>
                    <Doughnut data={clientsByStatusData} options={{ responsive: true, maintainAspectRatio: true }} />
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Clients Table */}
            <Card style={{ borderRadius: '12px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              <Card.Body>
                <span className='text-contrast' style={{ fontSize: '18px', fontWeight: '600', display: 'block', marginBottom: '16px' }}>
                  Lista de Clientes
                </span>
                <SmartTable
                  columns={clientsColumns}
                  data={clientsData.clientsList || []}
                  searchable={true}
                  pagination={true}
                  itemsPerPage={10}
                />
              </Card.Body>
            </Card>
          </>
        )}

        {/* Filter Drawer */}
        <FilterDrawer
          show={showFilterDrawer}
          onHide={() => setShowFilterDrawer(false)}
          onApply={(filters) => {
            setShowFilterDrawer(false);
            handleAdvancedFilters(filters);
          }}
          currentFilters={filters.advancedFilters}
        />
      </Container>
    </MasterLayout>
  );
};

export default ClientsReport;

